import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Bell, X } from 'lucide-react'
import { canDeleteAnySalonNote, SALON_NOTE_MAX, type SalonNote, type SalonRow } from '@manaro/contracts'
import { useSession } from '@/features/auth/use-session.ts'
import { cn } from '@/shared/lib/cn.ts'
import { Button } from '@/shared/ui/button.tsx'
import { Alert } from '@/shared/ui/states.tsx'
import { isNoteAuthor, noteTarget } from '../notes.ts'
import { formatDay, kyivToday } from '../reminders.ts'
import { useAddSalonNote } from '../use-salon-notes.ts'
import { NoteItem } from './note-item.tsx'
import { Counter, errorText, linkButtonClass, submitOnModEnter, textareaClass } from './note-parts.tsx'
import { ReminderPicker } from './reminder-picker.tsx'

/** A phone keyboard would cover the thread someone opened to read: focus the box there only when the thread is empty. */
const hasFinePointer = (): boolean => window.matchMedia('(pointer: fine)').matches

interface SalonNotesDialogProps {
  row: SalonRow
  notes: readonly SalonNote[]
  onClose: () => void
}

/** The team's comments on one row of «Салоны»: the newest on top, a box to add one above them. */
export function SalonNotesDialog({ row, notes, onClose }: SalonNotesDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const pressedOnBackdrop = useRef(false)
  const [draft, setDraft] = useState('')
  const [remindOn, setRemindOn] = useState<string | null>(null)
  const [choosingReminder, setChoosingReminder] = useState(false)
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set())
  const today = kyivToday()
  const add = useAddSalonNote()
  const me = useSession().data?.user
  const mayDeleteAny = me !== undefined && canDeleteAnySalonNote(me.access)
  const subtitle = [row.salon?.title, row.user.email].filter(Boolean).join(' · ')

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const close = () => {
    if (!add.isPending) dialogRef.current?.close()
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    const text = draft.trim()
    if (!text || add.isPending) return
    add.mutate(
      { ...noteTarget(row), text, remindOn },
      {
        onSuccess: () => {
          // Whatever was typed while the note was saving stays in the box.
          setDraft((current) => (current.trim() === text ? '' : current))
          setRemindOn(null)
          setChoosingReminder(false)
        },
      },
    )
  }

  const markBusy = (id: string, busy: boolean) =>
    setBusyIds((current) => {
      const next = new Set(current)
      if (busy) next.add(id)
      else next.delete(id)
      return next
    })

  const dirty = draft.trim() !== '' || busyIds.size > 0

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      // Esc and a click on the backdrop keep a typed draft or an open edit; the cross closes anyway.
      onCancel={(event) => (add.isPending || dirty) && event.preventDefault()}
      onMouseDown={(event) => {
        pressedOnBackdrop.current = event.target === event.currentTarget
      }}
      onClick={(event) => {
        if (pressedOnBackdrop.current && event.target === event.currentTarget && !dirty) close()
      }}
      aria-labelledby="salon-notes-title"
      className="m-auto max-h-[calc(100dvh-24px)] w-[min(560px,calc(100vw-24px))] overflow-y-auto overscroll-contain rounded-panel border border-line bg-surface p-0 text-ink shadow-panel backdrop:bg-[rgb(27_27_24/0.35)]"
    >
      <div className="flex flex-col gap-4 px-5 py-5 md:px-6">
        <header className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2
              id="salon-notes-title"
              className="font-display text-[19px] leading-tight font-semibold tracking-[-0.01em]"
            >
              Комментарии
            </h2>
            <p className="mt-0.5 text-[13px] text-ink-muted [overflow-wrap:anywhere]">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={add.isPending}
            aria-label="Закрыть"
            className="-mt-1.5 -mr-2 flex size-11 shrink-0 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink disabled:opacity-50 md:size-9"
          >
            <X className="size-5" aria-hidden />
          </button>
        </header>

        <form onSubmit={submit} className="flex flex-col gap-2">
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={submitOnModEnter}
            rows={3}
            maxLength={SALON_NOTE_MAX}
            autoFocus={notes.length === 0 || hasFinePointer()}
            aria-label="Новый комментарий"
            placeholder="Например: созвонились, просит перезвонить в пятницу после 15:00."
            className={textareaClass}
          />
          {choosingReminder && (
            <div className="flex flex-col gap-2 rounded-control bg-surface-hover/60 p-2.5">
              <span className="text-[12.5px] text-ink-soft">Напомнить вам — в этот день бэк-офис покажет окно</span>
              <ReminderPicker value={remindOn} today={today} onChange={setRemindOn} disabled={add.isPending} />
            </div>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <button
              type="button"
              aria-expanded={choosingReminder}
              onClick={() => {
                if (choosingReminder) setRemindOn(null)
                setChoosingReminder(!choosingReminder)
              }}
              className={cn(linkButtonClass, 'inline-flex items-center gap-1.5 text-[13px]', remindOn && 'text-brand-ink')}
            >
              <Bell className="size-3.5" aria-hidden />
              {remindOn ? `Напомню ${formatDay(remindOn)}` : choosingReminder ? 'Без напоминания' : 'Напомнить…'}
            </button>
            <p className="min-w-0 flex-1 basis-[200px] text-[12px] text-ink-muted text-balance-pretty">
              Видит вся команда, салон — нет.
              <span className="hidden md:inline"> ⌘/Ctrl + Enter — добавить.</span>
            </p>
            <Counter length={draft.length} />
            <Button type="submit" size="sm" loading={add.isPending} disabled={!draft.trim()} className="max-sm:min-h-11 max-sm:w-full">
              Добавить
            </Button>
          </div>
          {add.error && <Alert>{errorText(add.error)}</Alert>}
        </form>

        {notes.length > 0 ? (
          <ul className="divide-y divide-line-soft border-t border-line-soft">
            {notes.map((note) => {
              const own = isNoteAuthor(note, me?.email)
              return (
                <NoteItem
                  key={note.id}
                  note={note}
                  today={today}
                  own={own}
                  mayDelete={own || mayDeleteAny}
                  onBusyChange={(busy) => markBusy(note.id, busy)}
                />
              )
            })}
          </ul>
        ) : (
          <p className="border-t border-line-soft pt-4 text-[13.5px] text-ink-muted text-balance-pretty">
            Комментариев пока нет. Запишите, о чём договорились с владельцем, — коллеги увидят это прямо в списке салонов.
          </p>
        )}
      </div>
    </dialog>
  )
}
